import { useEffect, useRef, useState, type FormEvent } from 'react';

/** Served by public/api/contact.php on the Hostinger account. */
export const FORM_ENDPOINT = '/api/contact.php';

export type FormKind = 'consultation' | 'contact' | 'enquiry' | 'newsletter';
export type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Submit handler for every site form. Collects the form's named fields, adds the form
 * kind, a honeypot field ("website", must stay empty) and the time the form was shown
 * (bots submit instantly), then POSTs JSON to the PHP mailer.
 */
export function useFormSubmit(kind: FormKind) {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [error, setError] = useState('');
  const shownAt = useRef(0);

  // Browser-only value, set after hydration so the first render stays deterministic.
  useEffect(() => {
    shownAt.current = Date.now();
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data: Record<string, string> = { form: kind };
    new FormData(form).forEach((value, key) => {
      if (typeof value === 'string') data[key] = value.trim();
    });
    data.elapsed_ms = String(Date.now() - shownAt.current);
    data.page = window.location.pathname;

    setStatus('submitting');
    setError('');
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(data),
        cache: 'no-store',
      });
      const json = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
      if (!res.ok || !json.ok) throw new Error(json.message || 'Something went wrong. Please try again.');
      setStatus('success');
      form.reset();
    } catch (err) {
      setStatus('error');
      setError(
        err instanceof Error && err.message !== 'Failed to fetch'
          ? err.message
          : 'We could not send your message. Please call or WhatsApp us instead.',
      );
    }
  }

  return { status, error, onSubmit, reset: () => setStatus('idle') };
}
